import React, { useState, useEffect } from 'react';
import { feedback } from '../api';
import { COMPANY_INFO, getAddressLines } from '../config/constants';

const FeedbackPage = ({ user, setCurrentPage }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feedback'); // 'feedback', 'bug', 'other'

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      setCurrentPage('auth');
    }
  }, [user, setCurrentPage]);

  // If not authenticated, don't render the form
  if (!user) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTypeChange = (type) => {
    setFeedbackType(type);
    
    // Prefill subject based on feedback type
    let subject = '';
    switch (type) {
      case 'bug':
        subject = 'Bug Report: ';
        break;
      case 'feedback':
        subject = 'Feedback: ';
        break;
      default:
        subject = '';
    }
    
    setFormData({
      ...formData,
      subject,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Basic validation
    if (!formData.subject || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Submit feedback
      const response = await feedback.submitFeedback(formData);
      
      if (response.data.status === 'success') {
        setSuccess('Your feedback has been submitted successfully! Thank you.');
        // Reset form
        setFormData({
          ...formData,
          subject: '',
          message: '',
        });
        setFeedbackType('feedback');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err.response?.data?.detail || 'Failed to submit feedback. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900">
            Contact &amp; Feedback
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            We'd love to hear from you! Send us your feedback, report bugs, or just say hello.
          </p>
        </div>
        
        {/* Feedback type selection */}
        <div className="bg-white p-6 shadow rounded-lg mb-6">
          <div className="text-center mb-4">
            <p className="text-gray-700 font-medium">What type of message would you like to send?</p>
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => handleTypeChange('feedback')}
              className={`px-4 py-3 border rounded-lg text-center transition-colors ${
                feedbackType === 'feedback'
                  ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6 mx-auto mb-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
              Feedback & Suggestions
            </button>
            
            <button
              type="button"
              onClick={() => handleTypeChange('bug')}
              className={`px-4 py-3 border rounded-lg text-center transition-colors ${
                feedbackType === 'bug'
                  ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6 mx-auto mb-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Report a Bug
            </button>
            
            <button
              type="button"
              onClick={() => handleTypeChange('other')}
              className={`px-4 py-3 border rounded-lg text-center transition-colors ${
                feedbackType === 'other'
                  ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6 mx-auto mb-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              General Contact
            </button>
          </div>
        </div>
        
        {/* Form */}
        <div className="bg-white p-6 shadow rounded-lg">
          {success ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">Thank you!</h3>
              <p className="mt-2 text-sm text-gray-500">{success}</p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setSuccess('');
                  }}
                  className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  Send Another Message
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
  
              <div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    You are sending this message as: <span className="font-medium">{user.email}</span>
                  </p>
                  {user.full_name && (
                    <p className="text-sm text-gray-600">
                      Name: <span className="font-medium">{user.full_name}</span>
                    </p>
                  )}
                </div>
              </div>
  
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700"
                >
                  Subject *
                </label>
                <div className="mt-1">
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                  />
                </div>
              </div>
  
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700"
                >
                  Message *
                </label>
                <div className="mt-1">
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                    placeholder={
                      feedbackType === 'bug'
                        ? 'Please describe the issue in detail. What did you expect to happen and what actually happened? What steps can we take to reproduce the issue?'
                        : 'Write your message here...'
                    }
                  ></textarea>
                </div>
              </div>
  
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setCurrentPage('home')}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 mr-3"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    isSubmitting
                      ? 'bg-emerald-400 cursor-not-allowed'
                      : 'bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Company Contact Information */}
        <div className="mt-8 bg-white p-6 shadow rounded-lg">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Company Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-md font-medium text-gray-800 mb-2">Business Address</h3>
              <div className="text-sm text-gray-600 space-y-1">
                {getAddressLines().map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-md font-medium text-gray-800 mb-2">Contact Details</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Phone: {COMPANY_INFO.contact.phone}</p>
                <p>Email: {COMPANY_INFO.contact.email}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Help resources */}
        <div className="mt-8 bg-white p-6 shadow rounded-lg">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Other Ways to Get Help</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-md font-medium text-gray-800">Email Support</h3>
              <p className="text-sm text-gray-600">
                For urgent matters, you can contact our support team directly.
              </p>
              <a 
                href={`mailto:${COMPANY_INFO.contact.email}`} 
                className="mt-1 inline-block text-sm font-medium text-emerald-600 hover:text-emerald-500"
              >
                {COMPANY_INFO.contact.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
